import React, { FC, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'

import {
  NotionButton,
  NotionSelect,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import { useDrugGroupsQuery } from '@/features/internal/drugs/hooks/queries/useDrugGroupsQuery'

type Props = {
  onSubmit: (drugGroupId: string) => void
  onCancel: () => void
  isLoading?: boolean
}

// トレーシングレポート提出ルールの作成フォーム。
// 入力は既存の医薬品グループ (/portal/soaps/groups) の選択 1 つのみ。
// 編集は無し (ルールはグループ参照のみのため、削除して作り直す運用)。
// グループ管理への導線はページ actions スロット側 (design-book 04-apps/kakeru.md)。
// フォームに残すのはグループ 0 件時の作成分岐のみ (死に select を出さない)。
export const TracingReportRuleForm: FC<Props> = ({
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const navigate = useNavigate()
  const [drugGroupId, setDrugGroupId] = useState('')
  const [error, setError] = useState('')

  const drugGroupsQuery = useDrugGroupsQuery()
  const drugGroupOptions = (drugGroupsQuery.data?.results ?? []).map(
    (group) => ({
      value: group.id,
      label: group.name,
    })
  )

  // グループ 0 件では select が袋小路になるため、作成導線に差し替える
  if (drugGroupsQuery.isSuccess && drugGroupOptions.length === 0) {
    return (
      <Stack gap={2}>
        <Typography sx={PORTAL_TEXT.cellMeta}>
          医薬品グループがまだありません。先に作成してください
        </Typography>
        <Box display="flex" gap={2} justifyContent="flex-end">
          <NotionButton variant="secondary" onClick={onCancel}>
            キャンセル
          </NotionButton>
          <NotionButton
            variant="primary"
            onClick={() => navigate('/portal/soaps')}
          >
            医薬品グループを作成
          </NotionButton>
        </Box>
      </Stack>
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!drugGroupId) {
      setError('医薬品グループを選択してください')
      return
    }
    onSubmit(drugGroupId)
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack gap={2}>
        <Box>
          <NotionSelect<string>
            label="対象の医薬品グループ"
            value={drugGroupId}
            onChange={(value) => {
              setDrugGroupId(value)
              setError('')
            }}
            options={drugGroupOptions}
            placeholder="グループを選択"
            fullWidth
          />
          {error && (
            <Typography sx={{ ...PORTAL_TEXT.errorInline, mt: 0.5 }}>
              {error}
            </Typography>
          )}
        </Box>

        <Box display="flex" gap={2} justifyContent="flex-end">
          <NotionButton variant="secondary" onClick={onCancel}>
            キャンセル
          </NotionButton>
          <NotionButton type="submit" variant="primary" disabled={isLoading}>
            {isLoading ? '処理中...' : '作成'}
          </NotionButton>
        </Box>
      </Stack>
    </Box>
  )
}
